import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { IIntegrationUIConfig, IIntegrationUIToggleSection } from '@feathr/components';
import { SyncRuleSection } from '@feathr/components';

import { BLACKBAUD_ICON } from '../blackbaudIcon';
import {
  getActivityValues,
  stageDownsyncActivity,
} from '../RaisersEdgeActivitiesTab/RaisersEdgeActivitiesTab.utils';
import {
  CONSTITUENT_DOWNSYNC_KEY,
  CONSTITUENT_SYNC_SECTION_KEY,
  CREATE_CONSTITUENTS_SECTION_KEY,
} from '../raisersEdgeConfig';
import RaisersEdgeSyncSummary from '../RaisersEdgeSyncSummary';
import type { TRaisersEdgeSyncSummaryResult } from '../RaisersEdgeSyncSummary/RaisersEdgeSyncSummary.utils';
import RaisersEdgeTabContent from '../RaisersEdgeTabContent';
import type { IRaisersEdgeMappingDraft } from './RaisersEdgeMappingsTab.hooks';
import RaisersEdgeMappingTable from './RaisersEdgeMappingTable';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  /** The page config. The table reads its `mapping` region. */
  config: IIntegrationUIConfig;
  /** The config's toggle sections for this tab. */
  sections: readonly IIntegrationUIToggleSection[];
  /** The page's staged mapping edits. */
  draft: IRaisersEdgeMappingDraft;
  /** Disables every toggle with this reason, e.g. while the page saves. */
  disabledReason?: string;
  /** The page's summary bar values: `undefined` while loading, `null` if the load failed. */
  syncSummary?: TRaisersEdgeSyncSummaryResult;
}

function RaisersEdgeMappingsTab({
  config,
  disabledReason,
  draft,
  integration,
  sections,
  syncSummary,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const values = getActivityValues({ downsync_activities: integration.get('downsync_activities') });

  // Stage the edit on the model; the page's Save & Sync persists it. The create-constituent toggles
  // have no attribute yet, and are disabled.
  function handleChange(key: string, value: boolean): void {
    if (key === CONSTITUENT_DOWNSYNC_KEY) {
      stageDownsyncActivity(integration, 'constituents', value);
    }
  }

  function renderSection(key: string): JSX.Element | null {
    const section = sections.find((candidate) => candidate.key === key);
    if (!section) {
      return null;
    }
    return (
      <SyncRuleSection
        crmIcon={BLACKBAUD_ICON}
        disabledReason={disabledReason}
        name={`raisers-edge-${section.key}`}
        onChange={handleChange}
        section={section}
        values={values}
      />
    );
  }

  return (
    <RaisersEdgeTabContent
      inactiveLabel={t('Connect Blackbaud to choose which fields sync.')}
      integration={integration}
      // The mapping table's five columns need the wide width, as in Figma.
      isWide={true}
    >
      <RaisersEdgeSyncSummary summary={syncSummary} />
      {renderSection(CONSTITUENT_SYNC_SECTION_KEY)}
      <RaisersEdgeMappingTable config={config} draft={draft} integration={integration} />
      {renderSection(CREATE_CONSTITUENTS_SECTION_KEY)}
    </RaisersEdgeTabContent>
  );
}

export default observer(RaisersEdgeMappingsTab);
